/**
 * The Command Desk's two match panels — §6 `Today's Matches` and §7 `Tomorrow Onwards`.
 *
 * Both draw the same six columns, because §7 says so and because a TD who has learnt to read
 * one row should not have to learn the other. What differs is the grouping — today is one day
 * and the lookahead is many.
 *
 * **RUN 31 — the crew sheet is on BOTH panels now.** It used to be today's alone, on the
 * reasoning that *"a sheet on every remaining fixture would be a page of empty boxes for days
 * nobody has drafted yet"*. The Technical Delegate, testing the live system, asked for it on
 * `Tomorrow Onwards` as well, and he is right about his own job: the days he most needs to
 * open a sheet on are the ones he has not finished appointing. The empty-boxes worry was
 * about sheets that are open, and these are shut until he presses `Sheet` — and a shut sheet
 * costs nothing. Both panels build the sheet from the same {@link CrewSheetRow} and the same
 * `crewSheet` derivation, so the seven slots, the `Not appointed here` umpire boxes and the
 * profile links cannot drift between them.
 *
 * **RUN 82 — the crew line and its TMS chip (spec 01 §6a, amended 2026-09-17).** `Today's
 * Matches` rows now carry `TO · SJ · TJ` under the crew glance, with a chip saying what the crew
 * is to TMS: `Draft`, `🔒 On TMS`, or `Needs Re-Push`. It is the §A11 lock the TO Appointments
 * page writes, read from the day's appointments this panel already holds.
 *
 * **RUN 82C — the independent QA of that addendum.** Both panels take `now`, the page's minute,
 * because whether a match is finished is the desk's one played rule (`matchCompleted`) and no
 * longer the mirrored status alone: the `FT` pill, the `LIVE` chip, the settled row and the crew
 * glance's `Match completed` all read it. A Today's Matches row names its crew's TMS state in its
 * accessible name, and carries the chip beside its match number too, where it shows only on the
 * narrow screens that hide the crew column.
 *
 * **RUN 82G — the second independent QA.** A match is under way from its kick-off by the clock,
 * not only once TMS calls it live (`matchUnderWay`, M2): Today's Matches draws such a row live and
 * its glance says `In progress`, while the `LIVE` chip and the period pill still wait for TMS's own
 * word. Today's Matches' head note is dated `As Of HH:MM` from the day's own read (L2). And the
 * lookahead's foot no longer says a sheet "asks once" for its day: a press whose read failed is
 * asked again by the next press, and has been since RUN 31.
 *
 * **RUN 82J — the third independent QA (2026-09-18).** A day whose appointments have not been read
 * is no longer drawn as a day nobody has appointed (M3): until Today's Matches holds the day's own
 * read, a row says `Crew loading` or `Crew not loaded` where it said `TO, SJ, TJ Pending`, carries
 * no appointment pill, and a refused read is said once, in a quiet `Not Loaded` line. And a shut
 * crew sheet is `inert` (H2): its profile links were still in the tab order and read out by screen
 * readers, though the sheet was drawn at no height and no opacity.
 *
 * **RUN 82T — the final delta QA (H-1, 2026-09-18).** RUN 82J's honesty was `Today's Matches`' alone,
 * and `Tomorrow Onwards` went on asserting appointment state for days it could not read: a refused
 * `GET /appointments?day=` flipped its rows from `Appointed` to `Scheduled`, its crew boxes read
 * `Unfilled`, and nothing said the read had failed. It takes `reads` now — where each day on the
 * page stands — and says what the panel above says, in the same words: `Crew loading` / `Crew not
 * loaded` on the day header, no appointment pill and `Loading` / `Not loaded` boxes for a day not
 * read, and one quiet `status` line naming the days refused.
 *
 * **Fictional data only.** Nothing here holds a name; every name it prints came from the API.
 */

import { Fragment } from "react";
import { Link } from "react-router-dom";

import type { AppointmentFixtureBody, FixtureRowView } from "../../api/types.js";
import { EmptyState, FailBanner } from "../../app/states.js";
import { AsOfStamp } from "./AsOfStamp.js";
import {
  crewAtAGlance,
  crewLine,
  crewReadNote,
  crewSheet,
  deskStatus,
  matchCompleted,
  matchOwnStatus,
  matchUnderWay,
  shortDate,
  type CrewCell,
  type CrewLine,
  type CrewLockState,
  type DayReadState,
  type ReadStamp,
} from "./derive.js";

/**
 * RUN 82J's line for a day whose appointments were refused — `Today's Matches`' since the third QA
 * (M3), and `Tomorrow Onwards`' since RUN 82T (final delta QA, H-1, 2026-09-18).
 *
 * One function rather than two strings, so that the two halves of the page say the same thing in
 * the same words. The lookahead's refusal is the same refusal — `GET /appointments?day=` for a day
 * on screen — and a Technical Delegate who has read the sentence once under `Today's Matches`
 * should not meet a second wording under the panel below it. The only difference is how many days
 * it names: today is one, and the lookahead page is up to {@link LookaheadPanel}'s page size, so
 * the days are listed and `its`/`their` follows the count.
 *
 * (§9's Appointment Status says its own sentence, `windowNotCounted` — a different consequence, for
 * a figure rather than a row — and is deliberately not folded in here.)
 */
export function appointmentsNotRead(days: readonly string[]): string {
  const named = days.map(shortDate);
  const list =
    named.length <= 1
      ? (named[0] ?? "the day in view")
      : `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
  return (
    `The appointments for ${list} could not be read, so no crew, TMS chip or ` +
    `appointment status is shown for ${named.length <= 1 ? "its" : "their"} matches.`
  );
}

/**
 * §6's status pill, and §7's — one component so the two panels cannot drift apart.
 *
 * RUN 82J — `crews` (third QA, M3): until the day's appointments are `read`, the pill is the match's
 * own state alone — `FT`, or TMS's period — and otherwise there is no pill. `Scheduled` is what an
 * empty index says of a crew, and nobody has read this one.
 */
export function DeskStatusPill({
  fixture,
  appointed,
  now,
  crews = "read",
}: {
  fixture: FixtureRowView;
  appointed: AppointmentFixtureBody | null;
  /** RUN 82C — the page's minute, for the one played rule `FT` is read from. */
  now: Date;
  /** RUN 82J — where the day's own appointments read stands. The lookahead's rows leave it `read`. */
  crews?: DayReadState;
}): React.JSX.Element | null {
  const status =
    crews === "read" ? deskStatus(fixture, appointed, now) : matchOwnStatus(fixture, now);
  if (status === null) return null;
  return (
    <span className={`statuspill statuspill--${status.variant}`}>
      <span className="sdot" aria-hidden="true" />
      {status.label}
    </span>
  );
}

/** How the two halves of a fixture are named — the team, or the position it is filled from. */
function side(code: string | null, placeholder: string | null): string {
  return code ?? placeholder ?? "?";
}

/**
 * The four shared cells: match number, time, competition tag and the fixture itself.
 *
 * **RUN 82C.** `LIVE` is TMS's word, drawn only while the desk's one played rule has not called
 * the match finished — past a `live` status's hold the row reads `FT`, and a `LIVE` chip beside
 * an `FT` pill would be the two rules this change removed. `lock` is a Today's Matches row's crew
 * state, for the chip that stands in for the crew column where that column is hidden (≤900px);
 * the lookahead has no crew line and passes `null`.
 *
 * **RUN 82G — the chip stays TMS's.** A match the clock says is under way (`matchUnderWay`) is
 * drawn as a live ROW by `TodayPanel`, but gets no `LIVE` chip here unless TMS calls it live: the
 * chip reports TMS's status, and the row reports the clock.
 */
function FixtureCells({
  fixture,
  now,
  lock,
}: {
  fixture: FixtureRowView;
  now: Date;
  lock: CrewLockState | null;
}): React.JSX.Element {
  const men = fixture.competitionGender === "m";
  const live = fixture.tmsStatus === "live" && !matchCompleted(fixture, fixture.tmsStatus, now);
  const scored = fixture.homeScore !== null && fixture.awayScore !== null;
  return (
    <>
      <td className="cell-mno">
        {live ? (
          <span className="mno-wrap">
            {fixture.displayMatchNo}
            <span className="livechip" aria-label="Live now">
              <span className="ld" aria-hidden="true" />
              LIVE
            </span>
          </span>
        ) : (
          fixture.displayMatchNo
        )}
        {lock === null ? null : (
          <TmsChip state={lock} tmsMatchDbId={fixture.tmsMatchDbId} placement="match" />
        )}
      </td>
      <td className="cell-time">{fixture.time}</td>
      <td>
        <span className={men ? "comp-tag" : "comp-tag comp-tag--w"}>{men ? "MEN" : "WOMEN"}</span>
      </td>
      <td className="cell-teams">
        {side(fixture.homeTeamCode, fixture.homePlaceholder)}
        {scored ? (
          <span className="score">
            {fixture.homeScore}&ndash;{fixture.awayScore}
          </span>
        ) : (
          <span className="v">v</span>
        )}
        {side(fixture.awayTeamCode, fixture.awayPlaceholder)}
      </td>
    </>
  );
}

/**
 * RUN 82 — the chip at the end of the crew line (spec 01 §6a).
 *
 * The same three states, in the same vocabulary, as the TO Appointments page (§A11): `On TMS`
 * solid green where the pills are washes, because it is a record rather than a stage of work;
 * `Needs Re-Push` in that page's amber ink; `Draft` a grey outline. **The padlock is decoration
 * and is hidden from assistive technology**, so the chip says `Locked` in words for a screen
 * reader instead of leaving the glyph to carry it, and each chip's `title` gives the whole
 * sentence to a pointer.
 *
 * **RUN 82C — two placements, one chip.** `crew` is the chip at the end of the crew line.
 * `match` is the same chip in the match cell, for the widths at which the stylesheet hides the
 * crew column (≤900px): it is `display: none` above that width and the crew column is `display:
 * none` below it, so exactly one of the two is ever drawn — or read by a screen reader, which
 * follows `display` — at any width. Only the test id and one class tell them apart.
 */
function TmsChip({
  state,
  tmsMatchDbId,
  placement,
}: {
  state: CrewLockState;
  tmsMatchDbId: number;
  placement: "crew" | "match";
}): React.JSX.Element {
  const testId =
    placement === "crew"
      ? `tmschip-${String(tmsMatchDbId)}`
      : `tmschip-narrow-${String(tmsMatchDbId)}`;
  const narrow = placement === "crew" ? "" : " tmschip--narrow";
  if (state === "on_tms") {
    return (
      <span
        className={`tmschip tmschip--tms${narrow}`}
        data-testid={testId}
        data-state={state}
        title="Locked — this crew is the record TMS holds."
      >
        <span className="lock" aria-hidden="true">
          🔒
        </span>
        <span className="sr-only">Locked · </span>On TMS
      </span>
    );
  }
  if (state === "needs_push") {
    return (
      <span
        className={`tmschip tmschip--repush${narrow}`}
        data-testid={testId}
        data-state={state}
        title="Unlocked for a correction. TMS still holds the crew as it was locked — correct it there, confirm the day and lock it again."
      >
        Needs Re-Push
      </span>
    );
  }
  return (
    <span
      className={`tmschip tmschip--draft${narrow}`}
      data-testid={testId}
      data-state={state}
      title="Not locked yet — nothing here says TMS holds this crew."
    >
      Draft
    </span>
  );
}

/**
 * RUN 82C — a crew's TMS state in words, for a Today's Matches row's accessible name. The chip's
 * own words, so a screen reader and the screen say the same thing.
 */
const CREW_STATE_WORDS: Readonly<Record<CrewLockState, string>> = {
  on_tms: "On TMS",
  needs_push: "Needs Re-Push",
  draft: "Draft",
};

/**
 * §6's `Crew at a glance` cell.
 *
 * **RUN 82 (§6a).** The TO no longer rides in the first line: it heads the crew line under it,
 * beside the SJ and TJ, and printing it twice in one cell would be one name read as two facts.
 * A match with no technical crew at all draws no crew line and no chip — `crewLine` returns
 * null for it — so its row says exactly what it said before: `Pool A · TO, SJ, TJ Pending`.
 *
 * **RUN 82C.** `line` is handed in rather than derived here: the row reads the same line for its
 * accessible name and for the chip beside its match number, and one derivation per row is what
 * keeps the three from ever naming different states.
 *
 * **RUN 82J.** `crews` says whether the day's appointments have been read (M3); until they have,
 * the glance says `Crew loading` or `Crew not loaded`, and the row hands in no line.
 */
function CrewGlance({
  fixture,
  appointed,
  line,
  now,
  crews,
}: {
  fixture: FixtureRowView;
  appointed: AppointmentFixtureBody | null;
  line: CrewLine | null;
  now: Date;
  crews: DayReadState;
}): React.JSX.Element {
  const glance = crewAtAGlance(fixture, appointed, now, crews);
  return (
    <td className={line === null ? "cell-crew hide-sm" : "cell-crew hide-sm has-appt"}>
      {glance.stage}
      {" · "}
      {glance.noteIsWarning ? <span className="tbd">{glance.note}</span> : glance.note}
      {line === null ? null : (
        <span className="apptline" data-testid={`today-crew-${String(fixture.tmsMatchDbId)}`}>
          {line.slots.map((entry, index) => (
            <Fragment key={entry.slot}>
              {/* Flex items: the spaces are for the words, the gap is for the eye. */}
              {index === 0 ? null : (
                <>
                  {" "}
                  <span className="apsep" aria-hidden="true">
                    ·
                  </span>{" "}
                </>
              )}
              <span className="ap">
                <span className="aprole">{entry.slot}</span>{" "}
                {entry.name === null ? (
                  <span className="apnone">Unfilled</span>
                ) : (
                  <b>{entry.name}</b>
                )}
              </span>
            </Fragment>
          ))}{" "}
          <TmsChip state={line.state} tmsMatchDbId={fixture.tmsMatchDbId} placement="crew" />
        </span>
      )}
    </td>
  );
}

/**
 * One box of §6's seven-slot sheet.
 *
 * A filled slot's name is a **link to that official's profile** (module 05 Part B), not the
 * mockup's popover. §11 built a popover because there was no profile page and no route from a
 * match to a person; the popover's own fallback text points at Officials Profiles, and that
 * page now exists, so the link goes straight there rather than to a card that would have to
 * invent career statistics no route serves.
 *
 * RUN 82J — an `unread` box (M3) is neither: no amber wash, no mark, and `Not read` for a screen
 * reader, because nothing is known about the appointment in it.
 */
function Slot({ cell }: { cell: CrewCell }): React.JSX.Element {
  const tbd = cell.state === "unfilled" || cell.state === "no_store";
  return (
    <div className={tbd ? "slot slot--tbd" : "slot"}>
      <div className="slot__head">
        <span className="slot__role">{cell.slot}</span>
        {cell.state === "unread" ? null : (
          <span className="slot__mark" aria-hidden="true">
            {cell.state === "filled" ? "✅" : "⚠️"}
          </span>
        )}
        <span className="sr-only">
          {cell.state === "filled"
            ? "Appointed"
            : cell.state === "unfilled"
              ? "Not appointed"
              : cell.state === "unread"
                ? "Not read"
                : "No store for this slot yet"}
        </span>
      </div>
      <div className="slot__name">
        {cell.state === "filled" && cell.tmsPersonId !== null ? (
          <Link to={`/officials/${encodeURIComponent(cell.tmsPersonId)}`}>{cell.name}</Link>
        ) : (
          cell.name
        )}
      </div>
      <div className="slot__nat">{cell.nat}</div>
    </div>
  );
}

/**
 * The expandable seven-slot sheet under one fixture row — RUN 31, one component for both
 * panels.
 *
 * It was written inline in `TodayPanel`; `Tomorrow Onwards` now needs the same thing, and two
 * copies of a seven-box grid is two places for the `Not appointed here` wording, the `colSpan`
 * and the open/shut class to disagree. Nothing about the markup changed in the lifting.
 *
 * `appointed === null` — no record for the fixture at all, which is the normal state of a day
 * nobody has drafted — is not an error and draws no error: `crewSheet(null)` returns the seven
 * boxes with the three technical slots `Unfilled` and the four umpire slots saying plainly
 * that nothing appoints them here.
 *
 * **RUN 82J — a shut sheet is `inert` (third QA, H2, 2026-09-18).** The stylesheet shuts a sheet by
 * drawing it at `max-height: 0` and `opacity: 0`, so that it can open and shut as a transition, and
 * neither takes anything out of the tab order or the accessibility tree: every profile link of every
 * shut sheet was a Tab stop on an invisible name — one a filled slot, sixty on the QA's data — and a
 * screen reader read every shut sheet out. `inert` takes the whole row out of both while it is shut, and
 * changes nothing drawn, so the transition runs as it did. The row's `aria-controls` still names it.
 *
 * `crews` (RUN 82J, M3) is where the day's appointments read stands; until it is `read`, the three
 * technical boxes say so rather than `Unfilled`. The lookahead's sheets leave it `read`.
 */
export function CrewSheetRow({
  sheetId,
  open,
  appointed,
  crews = "read",
}: {
  sheetId: string;
  open: boolean;
  appointed: AppointmentFixtureBody | null;
  crews?: DayReadState;
}): React.JSX.Element {
  return (
    <tr className={open ? "xrow open" : "xrow"} id={sheetId} inert={!open}>
      <td colSpan={6}>
        <div className="xrow__inner">
          {crewSheet(appointed, crews).map((cell) => (
            <Slot key={cell.slot} cell={cell} />
          ))}
        </div>
      </td>
    </tr>
  );
}

/**
 * §6's `Today's Matches`.
 *
 * Rows expand rather than navigate, which is the mockup's behaviour and the right one: the
 * sheet answers *"who is on this match"* without losing the day the TD is reading.
 *
 * **RUN 82C — `addendum-copy`.** The words RUN 82 added to this panel's head note and foot, and
 * the ones RUN 82C adds, sit in spans of their own: the note and the foot are the desk's shared
 * chrome and are drawn in `--ink-3`, which measures under WCAG AA, and the stylesheet draws only
 * these added words in `--ink-2` rather than move the page-wide token under every other panel.
 *
 * **RUN 82G — `asOf`.** The crew lines and chips are the day's own read, refreshed on its own, so
 * the head note ends with that read's `As Of HH:MM` ({@link AsOfStamp}) — not the schedule-wide
 * read's, which §8b dates and which can answer at a different moment.
 *
 * **RUN 82J — `crews` (third QA, M3).** Where the day's own read stands. Until it is `read`, nothing
 * on a row is a claim about its crew: the glance says `Crew loading` or `Crew not loaded`, there is
 * no crew line or TMS chip, the pill is the match's own `FT` or period or nothing, and an opened
 * sheet's technical boxes say the same. The index cannot tell a crew nobody has read from a crew
 * nobody has appointed, and until RUN 82J the panel drew the first as the second — `TO, SJ, TJ
 * Pending` and `Scheduled` on six locked matches, for as long as a refused read stayed refused. A
 * refusal is said once for the panel, under its head, in the quiet `status` line the Alerts panel
 * already uses for the same refusal. While the read is merely out, the rows' own words say so.
 */
export function TodayPanel({
  day,
  fixtures,
  appointed,
  expanded,
  onToggle,
  pitch,
  openingDay,
  now,
  asOf,
  crews,
}: {
  day: string | null;
  fixtures: readonly FixtureRowView[];
  appointed: ReadonlyMap<number, AppointmentFixtureBody>;
  expanded: number | null;
  onToggle: (tmsMatchDbId: number) => void;
  pitch: string | null;
  openingDay: string | null;
  /** RUN 82C — the page's minute, for the one played rule the rows are drawn from. */
  now: Date;
  /** RUN 82G — when the day's own appointments read last answered; null before it has. */
  asOf: ReadStamp | null;
  /** RUN 82J — whether the day's own appointments have been read; a day with no fixtures has. */
  crews: DayReadState;
}): React.JSX.Element {
  return (
    <section className="panel panel--d1" aria-labelledby="todayTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="todayTitle">
          Today&rsquo;s Matches
        </h3>
        <span className="panel__note">
          {day === null ? "No match day" : shortDate(day)} · Click a row for the appointment
          sheet ·{" "}
          <span className="addendum-copy">TO · SJ · TJ with TMS push state</span>
          {asOf === null ? null : (
            <>
              {" · "}
              <AsOfStamp stamp={asOf} testId="today-asof" />
            </>
          )}
        </span>
        <span className="spacer" />
        {pitch === null ? null : <span className="kicker">{pitch}</span>}
      </div>

      {/* RUN 82T — the sentence is `appointmentsNotRead` now, which `Tomorrow Onwards` says too
          (final delta QA, H-1). The words for one day are RUN 82J's, unchanged. */}
      {crews !== "failed" || day === null || fixtures.length === 0 ? null : (
        <FailBanner error={new Error(appointmentsNotRead([day]))} role="status" />
      )}

      {fixtures.length === 0 ? (
        <EmptyState
          title="No Matches Today"
          detail={
            openingDay === null
              ? "This installation holds no fixtures yet. Import the tournament first; Tomorrow Onwards below will fill in with it."
              : `The tournament's opening match day is ${shortDate(openingDay)}. Tomorrow Onwards below shows the schedule from here.`
          }
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="mtable" aria-label="Today's matches">
            <thead>
              <tr>
                <th scope="col">Match</th>
                <th scope="col">Time</th>
                <th scope="col">Comp</th>
                <th scope="col">Fixture</th>
                <th scope="col" className="hide-sm">
                  Crew at a glance
                </th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {fixtures.map((fixture) => {
                // RUN 82J (M3) — no record, and no line, until the day's crews have been read.
                const record = crews === "read" ? (appointed.get(fixture.tmsMatchDbId) ?? null) : null;
                const open = expanded === fixture.tmsMatchDbId;
                // RUN 82C — the settled row and the live one, by the desk's one played rule.
                // RUN 82G — and the live one from its kick-off, not from TMS's `live` alone.
                const done = matchCompleted(fixture, fixture.tmsStatus, now);
                const live = matchUnderWay(fixture, fixture.tmsStatus, now);
                const rowClass = [done ? "is-done" : "", live ? "is-live" : ""]
                  .filter((entry) => entry !== "")
                  .join(" ");
                const sheetId = `crew-${String(fixture.tmsMatchDbId)}`;
                const line = crews === "read" ? crewLine(record) : null;
                return (
                  <Fragment key={fixture.tmsMatchDbId}>
                    {/* The whole row is the sheet toggle, matching Tomorrow Onwards — the
                        TD asked for the SHEET pill to go here too (2026-08-27). RUN 82C: its
                        name carries the crew's TMS state, which a screen reader otherwise
                        reaches only by leaving the row for its crew cell. */}
                    <tr
                      data-comp={fixture.competitionGender}
                      data-testid={`today-${String(fixture.tmsMatchDbId)}`}
                      className={["rowtoggle", open ? "is-open" : "", rowClass]
                        .filter((entry) => entry !== "")
                        .join(" ")}
                      tabIndex={0}
                      aria-expanded={open}
                      aria-controls={sheetId}
                      aria-label={`Toggle appointment sheet for ${fixture.displayMatchNo}${
                        line === null ? "" : `, crew ${CREW_STATE_WORDS[line.state]}`
                      }`}
                      onClick={() => {
                        onToggle(fixture.tmsMatchDbId);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onToggle(fixture.tmsMatchDbId);
                        }
                      }}
                    >
                      <FixtureCells
                        fixture={fixture}
                        now={now}
                        lock={line === null ? null : line.state}
                      />
                      <CrewGlance
                        fixture={fixture}
                        appointed={record}
                        line={line}
                        now={now}
                        crews={crews}
                      />
                      <td>
                        <DeskStatusPill
                          fixture={fixture}
                          appointed={record}
                          now={now}
                          crews={crews}
                        />
                      </td>
                    </tr>
                    <CrewSheetRow sheetId={sheetId} open={open} appointed={record} crews={crews} />
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="panel__foot">
        <b>Three of the seven crew boxes are filled from this desk.</b> TO, SJ and TJ come from
        the appointments of module 01. U1, U2, RU and VU belong to the Umpires Managers, and
        this build shows them read-only on{" "}
        <b>Umpire Appointments</b> (module 02) rather than serving them here — open that page to
        see who is recorded in each of the four, and whether the duties are being spread fairly.{" "}
        <span className="addendum-copy">
          <b>The chip after TO · SJ · TJ is the day&rsquo;s lock</b> from TO Appointments:{" "}
          <b>Draft</b> until the day is marked locked, <b>On TMS</b> once it is, and{" "}
          <b>Needs Re-Push</b> for a match unlocked for a correction TMS does not hold yet. The
          desk reads it again every five minutes and whenever this tab or window comes back into
          use; <b>As Of</b> in the head is when it last answered.
        </span>
      </div>
    </section>
  );
}

/** `4` → `Four`, for the panel foot's sentence. Past six it prints the figure. */
const DAY_COUNT_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six"];

/**
 * §7's `Tomorrow Onwards`.
 *
 * `Days per page` is §7's Configuration §07 field `Fixtures Lookahead — Days Shown Under
 * Tomorrow Onwards`, default 3. The configuration store has no such field (see the Task 3
 * report), so the default stands and the panel foot says where it would come from — a
 * segmented control writing to nothing would be a setting that silently forgot itself.
 *
 * **RUN 82 (spec 01 §7a, amended 2026-09-17).** The page is FOUR days now — the Technical
 * Delegate's Command Desk addendum, part of rebalancing the two columns once Cards Listing
 * moved to the rail. The number still lives in code, in `CommandDeskPage`, and arrives here as
 * `daysPerPage` so the foot states the figure the pager actually uses rather than a copy of it.
 *
 * **RUN 82C — where the four comes from, said accurately.** The foot used to call it the
 * Technical Delegate's choice of 17 September. What he approved that day was the mockup, whose
 * own script still pages by three; four is the Run 82 work order's `3→4`. So the foot now says it
 * follows the work order, and nothing on the page says the two columns were measured level with
 * it — no measurement has been made at four days a page, in the mockup or in this build.
 *
 * **RUN 31 — rows expand, exactly as today's do.** `expanded` / `onToggle` mirror `TodayPanel`
 * slot for slot, with one addition: `onToggle` also carries the fixture's DATE, because the
 * page fetches a day's appointments only once a row on it has been opened and it cannot learn
 * the day from a match id. The pager and the competition tabs are untouched — they change
 * which rows exist, and a row that stops existing simply takes its shut sheet with it.
 *
 * **RUN 82T — `reads`, and the same honesty `Today's Matches` has had since RUN 82J (final delta
 * QA, H-1, 2026-09-18).** RUN 82J stopped a day nobody had read being drawn as a day nobody had
 * appointed — but only on the panel above this one. Here the rows went on asserting appointment
 * state for days the desk had never read: a refused `GET /appointments?day=` flipped them from
 * `Appointed` to **`Scheduled`**, their crew boxes read `Unfilled`, and nothing said the read had
 * failed. A Technical Delegate on a venue connection saw tomorrow's confirmed crews as unappointed.
 *
 * `reads` is where EACH day on the page stands, keyed by day, and the panel now says the same
 * things in the same words as `Today's Matches`: no appointment pill for a day not read (the Status
 * cell carries the match's own `FT` or TMS period, or nothing), `Loading` / `Not loaded` in an
 * opened sheet's three technical boxes, `Crew loading` / `Crew not loaded` on the day header the
 * rows are grouped under — the crew glance's own words ({@link crewReadNote}), since this panel has
 * no crew column to put them in — and one quiet `status` line under the head naming the days that
 * were refused ({@link appointmentsNotRead}). Each day is read on its own, so one refusal leaves
 * the other days on the page reading exactly as they did.
 */
export function LookaheadPanel({
  days,
  appointed,
  reads,
  page,
  pageCount,
  daysPerPage,
  onPage,
  tabs,
  highlightDay,
  expanded,
  onToggle,
  now,
}: {
  days: readonly { date: string; fixtures: readonly FixtureRowView[] }[];
  appointed: ReadonlyMap<number, AppointmentFixtureBody>;
  /**
   * RUN 82T — where each day on the page stands, by day (final delta QA, H-1). A day the map does
   * not name is `loading`: nothing is known of it, and nothing about it may be claimed.
   */
  reads: ReadonlyMap<string, DayReadState>;
  page: number;
  pageCount: number;
  /** RUN 82 — how many match days one page holds, as the page's own pager math uses it. */
  daysPerPage: number;
  onPage: (page: number) => void;
  tabs: React.ReactNode;
  highlightDay: string | null;
  /** The one open sheet, by match id — one at a time, as §6's panel has always behaved. */
  expanded: number | null;
  onToggle: (tmsMatchDbId: number, date: string) => void;
  /** RUN 82C — the page's minute, for the one played rule the pills are drawn from. */
  now: Date;
}): React.JSX.Element {
  const first = days[0]?.date ?? null;
  const last = days[days.length - 1]?.date ?? null;
  /**
   * RUN 82T — the days on this page whose own read was refused, in the order they are drawn. Only
   * days that have rows: a day with none says nothing, as `Today's Matches` says nothing about a
   * day with no fixtures.
   */
  const refused = days
    .filter((group) => reads.get(group.date) === "failed" && group.fixtures.length > 0)
    .map((group) => group.date);
  return (
    <section className="panel panel--d2" aria-labelledby="tmrwTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="tmrwTitle">
          Tomorrow Onwards
        </h3>
        <span className="panel__note">
          {first === null || last === null
            ? "Nothing ahead"
            : `${shortDate(first)} → ${shortDate(last)} · Click a row for the appointment sheet`}
        </span>
        <span className="spacer" />
        {tabs}
      </div>

      {/* RUN 82T (H-1) — said once for the panel, in `Today's Matches`' words and with its role:
          a `status`, because the desk goes on drawing and nothing here is an emergency. */}
      {refused.length === 0 ? null : (
        <FailBanner error={new Error(appointmentsNotRead(refused))} role="status" />
      )}

      <div className="tabpanel">
        {days.length === 0 ? (
          <EmptyState
            title="Nothing Ahead"
            detail="Every remaining fixture under the current competition filter has been played, or the schedule ends here."
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="mtable" aria-label="Remaining fixtures, grouped by day">
              <thead>
                <tr>
                  <th scope="col">Match</th>
                  <th scope="col">Time</th>
                  <th scope="col">Comp</th>
                  <th scope="col">Fixture</th>
                  <th scope="col" className="hide-sm">
                    Pool / Stage
                  </th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {days.map((group) => (
                  <LookaheadDay
                    key={group.date}
                    date={group.date}
                    fixtures={group.fixtures}
                    appointed={appointed}
                    /* RUN 82T (H-1) — a day the map does not name is `loading`, never `read`. */
                    crews={reads.get(group.date) ?? "loading"}
                    highlighted={group.date === highlightDay}
                    expanded={expanded}
                    onToggle={onToggle}
                    now={now}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pager" aria-label="Fixture pages">
          <span className="pager__info num">
            Page {page} of {pageCount}
          </span>
          <button
            type="button"
            aria-label="Previous fixture days"
            disabled={page <= 1}
            onClick={() => {
              onPage(page - 1);
            }}
          >
            &lsaquo; Prev
          </button>
          <button
            type="button"
            aria-label="Next fixture days"
            disabled={page >= pageCount}
            onClick={() => {
              onPage(page + 1);
            }}
          >
            Next &rsaquo;
          </button>
        </div>
      </div>

      <div className="panel__foot">
        <b>
          {DAY_COUNT_WORDS[daysPerPage] ?? String(daysPerPage)} days a page.
        </b>{" "}
        §7 takes that number from Tournament Configuration&rsquo;s{" "}
        <i>Fixtures Lookahead — Days Shown Under Tomorrow Onwards</i>, which the configuration
        store does not yet hold a field for, so it is set here
        <span className="addendum-copy">
          , following the Run 82 work order rather than the specification&rsquo;s default of
          three
        </span>
        . Appointment state is shown for the days on this page; turning the pager fetches the
        next set
        <span className="addendum-copy">
          , and the days on screen are read again every five minutes and whenever this tab or
          window comes back into use
        </span>
        . <b>Click any row</b> for that fixture&rsquo;s seven-slot appointment sheet — the same
        sheet Today&rsquo;s Matches opens.{" "}
        <span className="addendum-copy">
          Where this page&rsquo;s days could not be read, opening a sheet asks for its own day —
          once if that read answers, and again at the next press if it fails.{" "}
          <b>Each day on the page is read on its own</b>, so a day that could not be read says so
          here and the rest of the page is unaffected.
        </span>
      </div>
    </section>
  );
}

/**
 * One day of `Tomorrow Onwards` — its header row, and the fixtures under it.
 *
 * **RUN 82T — `crews` (final delta QA, H-1, 2026-09-18).** Where this day's own appointments read
 * stands. Until it is `read`, nothing under this header is a claim about a crew: no record reaches
 * a row, so there is no appointment pill and an opened sheet's technical boxes say `Loading` or
 * `Not loaded` — the same treatment, from the same `crews` prop, that `Today's Matches` has had
 * since RUN 82J. The header itself carries the glance's own two words, because this panel's fifth
 * column is the stage and there is nowhere else on a row to put them; one line above four or six
 * rows also says it once rather than once per match, which is the fact's real shape — the read is
 * per DAY.
 */
function LookaheadDay({
  date,
  fixtures,
  appointed,
  crews,
  highlighted,
  expanded,
  onToggle,
  now,
}: {
  date: string;
  fixtures: readonly FixtureRowView[];
  appointed: ReadonlyMap<number, AppointmentFixtureBody>;
  /** RUN 82T — where this day's own appointments read stands (H-1). */
  crews: DayReadState;
  highlighted: boolean;
  expanded: number | null;
  onToggle: (tmsMatchDbId: number, date: string) => void;
  now: Date;
}): React.JSX.Element {
  const pitches = [
    ...new Set(fixtures.map((fixture) => fixture.pitch).filter((pitch) => pitch !== null)),
  ];
  const unread = crewReadNote(crews);
  return (
    <>
      <tr
        className={highlighted ? "dayhead dayhead--today" : "dayhead"}
        {...(highlighted ? { id: "dayJump" } : {})}
      >
        <td colSpan={6}>
          {shortDate(date)} &nbsp;
          <span className="cnt" data-testid={`ahead-day-${date}`}>
            · {fixtures.length} {fixtures.length === 1 ? "match" : "matches"}
            {pitches.length === 0 ? "" : ` · ${pitches.join(", ")}`}
            {unread === null ? "" : ` · ${unread}`}
          </span>
        </td>
      </tr>
      {fixtures.map((fixture) => {
        // RUN 82T (H-1) — no record until this day's crews have been read, as `TodayPanel` does.
        const record = crews === "read" ? (appointed.get(fixture.tmsMatchDbId) ?? null) : null;
        const open = expanded === fixture.tmsMatchDbId;
        /*
         * A separate id space from today's `crew-…`. The two panels never hold the same
         * fixture — one is the focus day and the other is strictly after it — but an
         * `aria-controls` that could collide is a bug waiting for the day that assumption
         * stops being true.
         */
        const sheetId = `crew-ahead-${String(fixture.tmsMatchDbId)}`;
        return (
          <Fragment key={fixture.tmsMatchDbId}>
            {/* The TD asked for the whole row as the control, no SHEET pill here — the row
                itself toggles the sheet (2026-08-27). Today's panel keeps its pill: its
                status cell also carries LIVE state, and its rows were not the complaint. */}
            <tr
              data-comp={fixture.competitionGender}
              data-testid={`ahead-${String(fixture.tmsMatchDbId)}`}
              className={open ? "rowtoggle is-open" : "rowtoggle"}
              tabIndex={0}
              aria-expanded={open}
              aria-controls={sheetId}
              aria-label={`Toggle appointment sheet for ${fixture.displayMatchNo}`}
              onClick={() => {
                onToggle(fixture.tmsMatchDbId, date);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onToggle(fixture.tmsMatchDbId, date);
                }
              }}
            >
              <FixtureCells fixture={fixture} now={now} lock={null} />
              <td className="cell-pool hide-sm">{fixture.stage}</td>
              <td>
                <DeskStatusPill fixture={fixture} appointed={record} now={now} crews={crews} />
              </td>
            </tr>
            <CrewSheetRow sheetId={sheetId} open={open} appointed={record} crews={crews} />
          </Fragment>
        );
      })}
    </>
  );
}
